import { useState } from 'react'

import { G, fallStep, ru } from '../../lib/physics'
import { Arrow, ArrowDefs, Predict, Slider, useFrames } from './common'

/**
 * Книга на столе (урок 36): три силы и два тела. Сила тяжести и сила упругости стола
 * приложены к книге, вес — к столу. Выдерни стол — книга падает, вес и сила упругости
 * пропадают, сила тяжести остаётся.
 */

const СТОЛ_Y = 170
const PX_PER_N = 4

type Сила = 'тяжесть' | 'упругость' | 'вес'

export default function BookLab() {
  const [m, setM] = useState(1)
  const [показ, setПоказ] = useState<Record<Сила, boolean>>({ тяжесть: true, упругость: false, вес: false })
  const [падение, setПадение] = useState<{ y: number; v: number } | null>(null)

  useFrames((dt) => {
    if (!падение) return
    setПадение((p) => {
      if (!p || p.y > 1.2) return p
      const [y, v] = fallStep(p.y, p.v, m, 0, dt / 2)
      return { y, v }
    })
  })

  const наСтоле = падение === null
  const fТяж = m * G
  const N = наСтоле ? fТяж : 0
  const P = наСтоле ? fТяж : 0
  const прогиб = наСтоле ? Math.min(m * 1.6, 8) : 0
  const книгаY = СТОЛ_Y + прогиб - 30 + (падение ? падение.y * 100 : 0)
  const включить = (s: Сила) => setПоказ((x) => ({ ...x, [s]: !x[s] }))

  return (
    <div className="card">
      <svg className="lab-svg" viewBox="0 0 360 300" role="img" aria-label="Книга на столе и силы">
        <ArrowDefs />
        <line x1={0} y1={292} x2={360} y2={292} className="ink" strokeWidth={2} />
        {наСтоле && (
          <>
            <path d={`M60 ${СТОЛ_Y} Q180 ${СТОЛ_Y + 2 * прогиб} 300 ${СТОЛ_Y}`} className="ink" strokeWidth={8} fill="none" strokeLinecap="round" />
            <line x1={75} y1={СТОЛ_Y} x2={75} y2={292} className="ink" strokeWidth={6} />
            <line x1={285} y1={СТОЛ_Y} x2={285} y2={292} className="ink" strokeWidth={6} />
          </>
        )}
        <rect x={130} y={книгаY} width={100} height={26} rx={3} className="fill-book ink" strokeWidth={1.5} />
        <text x={180} y={книгаY + 17} textAnchor="middle" className="lab-label">{ru(m, 1)} кг</text>
        {показ.тяжесть && (
          <>
            <circle cx={165} cy={книгаY + 13} r={3} className="fill-heavy" />
            <Arrow x1={165} y1={книгаY + 13} x2={165} y2={книгаY + 13 + fТяж * PX_PER_N} kind="heavy" label="F тяж" labelDx={-50} labelDy={0} />
          </>
        )}
        {показ.упругость && наСтоле && (
          <>
            <circle cx={195} cy={книгаY + 26} r={3} className="fill-el" />
            <Arrow x1={195} y1={книгаY + 26} x2={195} y2={книгаY + 26 - N * PX_PER_N} kind="el" label="N (упр. стола)" labelDx={6} labelDy={8} />
          </>
        )}
        {показ.вес && наСтоле && (
          <>
            <circle cx={215} cy={СТОЛ_Y + прогиб} r={3} className="fill-band" />
            <Arrow x1={215} y1={СТОЛ_Y + прогиб} x2={215} y2={СТОЛ_Y + прогиб + P * PX_PER_N} kind="band" label="P (вес)" labelDx={6} labelDy={0} />
          </>
        )}
      </svg>

      <div className="lab-readout lab-readout-col">
        <span className="heavy">Сила тяжести F тяж = m·g = {ru(fТяж, 0)} Н — приложена к книге (притяжение Земли)</span>
        <span className="el">Сила упругости стола N = {ru(N, 0)} Н — приложена к книге (стол прогнулся)</span>
        <span className="band">Вес книги P = {ru(P, 0)} Н — приложен к столу (книга давит на него)</span>
      </div>
      {!наСтоле && <div className="lab-caption">Книга падает: опоры нет — веса нет, это невесомость. Сила тяжести осталась.</div>}

      <Predict
        question="К чему приложен вес книги?"
        options={['к книге', 'к столу', 'к Земле']}
        right={1}
        revealed={показ.вес}
        explain="Вес — сила, с которой книга давит на опору. Поэтому он приложен к столу, а сила тяжести и сила упругости стола — к самой книге."
      />

      <div className="lab-sub">Показать силы</div>
      <div className="row-wrap lab-choice">
        {(['тяжесть', 'упругость', 'вес'] as const).map((s) => (
          <button key={s} className={показ[s] ? 'btn btn-primary' : 'btn'} onClick={() => включить(s)}>
            {s === 'тяжесть' ? 'сила тяжести' : s === 'упругость' ? 'сила упругости стола' : 'вес'}
          </button>
        ))}
      </div>
      <Slider label="Масса книги" value={m} min={0.2} max={3} step={0.1} unit="кг" shown={ru(m, 1)} onChange={setM} />
      <div className="row-wrap" style={{ marginTop: 10 }}>
        {наСтоле ? (
          <button className="btn" onClick={() => setПадение({ y: 0, v: 0 })}>🫳 Выдернуть стол</button>
        ) : (
          <button className="btn" onClick={() => setПадение(null)}>↩️ Вернуть стол</button>
        )}
      </div>
      <p className="muted small">
        Вес и сила тяжести равны по величине, когда книга лежит на неподвижном столе, но это
        разные силы: разной природы и приложены к разным телам.
      </p>
    </div>
  )
}
